import { useEffect, useRef, type RefObject } from 'react';
import type { LinkedContextItem } from '../../../contracts/projects/linked-context';
import {
  resolveLinkedContextRef,
  toPersistedLinkedContextRef,
  type PersistedLinkedContextRef,
} from '../../../contracts/projects/linked-context';
import {
  isProjectsComposerDraftEmpty,
  type ProjectsComposerPromptDraft,
} from '../projects-composer-editor-draft';

// XXX: v2 drafts may hold `object` segments and `template` refs that a v1 build
// cannot read (a template ref falls through its id-blind patient lookup); v3 adds
// `task` and `collection` tokens a v2 build has no chip for. Bump again whenever
// the persisted shape widens, so a rollback never reads them.
const STORAGE_PREFIX = 'heidi-work:composer-draft:v3:';

/** Bumped by an explicit discard so an in-flight persist timeout cannot rewrite. */
const persistEpochByKey = new Map<string, number>();

const persistEpoch = (storageKey: string): number =>
  persistEpochByKey.get(storageKey) ?? 0;

const bumpPersistEpoch = (storageKey: string): void => {
  persistEpochByKey.set(storageKey, persistEpoch(storageKey) + 1);
};

interface PersistedComposerDraft {
  draft: ProjectsComposerPromptDraft;
  linkedContextRef: PersistedLinkedContextRef | null;
}

/** The one editor call a restore makes; the composer's prompt editor handle has it. */
interface DraftRestoreEditor {
  replaceDraft: (draft: ProjectsComposerPromptDraft) => void;
}

interface UseComposerDraftPersistenceArgs {
  storageKey: string;
  enabled: boolean;
  draft: ProjectsComposerPromptDraft;
  linkedItems: readonly LinkedContextItem[];
  linkableItems: readonly LinkedContextItem[];
  editorRef: RefObject<DraftRestoreEditor | null>;
  onRestoreLinkedItem: (item: LinkedContextItem | null) => void;
  onDraftRestored: (draft: ProjectsComposerPromptDraft) => void;
}

export const composerDraftStorageKey = (projectId: string): string =>
  `${STORAGE_PREFIX}${projectId}`;

export const clearComposerDraftStorage = (storageKey: string): void => {
  bumpPersistEpoch(storageKey);
  try {
    sessionStorage.removeItem(storageKey);
  } catch {
    // Private browsing / quota — draft persistence is best-effort.
  }
};

export const useComposerDraftPersistence = ({
  storageKey,
  enabled,
  draft,
  linkedItems,
  linkableItems,
  editorRef,
  onRestoreLinkedItem,
  onDraftRestored,
}: UseComposerDraftPersistenceArgs): { clearPersistedDraft: () => void } => {
  const hydratedRef = useRef(false);
  const skipNextPersistRef = useRef(false);

  useEffect(() => {
    if (!enabled || hydratedRef.current) return;

    hydratedRef.current = true;
    try {
      const raw = sessionStorage.getItem(storageKey);
      if (!raw) return;
      const parsed = JSON.parse(raw) as PersistedComposerDraft;
      if (!parsed.draft || isProjectsComposerDraftEmpty(parsed.draft)) return;
      skipNextPersistRef.current = true;
      editorRef.current?.replaceDraft(parsed.draft);
      onDraftRestored(parsed.draft);
      const restoredLinkedItem = parsed.linkedContextRef
        ? resolveLinkedContextRef(parsed.linkedContextRef, linkableItems)
        : null;
      onRestoreLinkedItem(restoredLinkedItem);
    } catch {
      // Corrupt storage — ignore.
    }
  }, [
    enabled,
    editorRef,
    linkableItems,
    onDraftRestored,
    onRestoreLinkedItem,
    storageKey,
  ]);

  useEffect(() => {
    if (!enabled || !hydratedRef.current) return;
    if (skipNextPersistRef.current) {
      skipNextPersistRef.current = false;
      return;
    }

    const isEmpty = isProjectsComposerDraftEmpty(draft) && linkedItems.length === 0;
    const epoch = persistEpoch(storageKey);
    const timer = window.setTimeout(() => {
      if (persistEpoch(storageKey) !== epoch) return;
      try {
        if (isEmpty) {
          sessionStorage.removeItem(storageKey);
          return;
        }
        const payload: PersistedComposerDraft = {
          draft,
          linkedContextRef: toPersistedLinkedContextRef(linkedItems[0]),
        };
        sessionStorage.setItem(storageKey, JSON.stringify(payload));
      } catch {
        // Best-effort only.
      }
    }, 300);

    return () => window.clearTimeout(timer);
  }, [draft, enabled, linkedItems, storageKey]);

  const clearPersistedDraft = () => {
    clearComposerDraftStorage(storageKey);
  };

  return { clearPersistedDraft };
};
